import { Fragment, useEffect, useRef } from "react";
import { useMessages, usePartsMap, useStreaming } from "../stores";
import { useAgentDraftStore } from "@/modules/agents/agent-draft-store";
import { TurnView } from "./TurnView";

const REATTACH_THRESHOLD_PX = 24;

interface Props {
  activeId: string | null;
}

export function MessageList({ activeId }: Props) {
  const messages = useMessages();
  const partsMap = usePartsMap();
  const isStreaming = useStreaming();
  // Suppress the "выберите сессию" placeholder while a draft from
  // SpaceDashboard is being turned into a real session — sendMessage hasn't
  // flipped activeId yet, but we know one is on the way.
  const starting = useAgentDraftStore((s) => s.starting);
  // Hero folder-sync activity rendered as a step in the turn rail: the
  // optimistic user bubble (while gating) + a tool-step that pulses while
  // `uploading` and settles to `completed` once `done`. Keyed by session.
  const heroSync = useAgentDraftStore((s) => s.heroSync);
  const containerRef = useRef<HTMLDivElement>(null);
  const stickRef = useRef(true);
  const prevTopRef = useRef(0);
  const lastProgScrollAtRef = useRef(0);
  const lastUserMsgIdRef = useRef<string | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => {
      const top = el.scrollTop;
      const distance = el.scrollHeight - top - el.clientHeight;
      // Detach is unconditional — any user scroll-up wins instantly.
      if (top < prevTopRef.current) {
        stickRef.current = false;
      } else if (
        distance <= REATTACH_THRESHOLD_PX &&
        performance.now() - lastProgScrollAtRef.current > 80
      ) {
        // Re-attach only outside the programmatic-scroll window, so our
        // own auto-scroll events can't override a fresh user detach.
        stickRef.current = true;
      }
      prevTopRef.current = top;
    };
    const onWheel = (e: WheelEvent) => {
      if (e.deltaY < 0) stickRef.current = false;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    el.addEventListener("wheel", onWheel, { passive: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      el.removeEventListener("wheel", onWheel);
    };
  }, []);

  const scrollToBottom = (el: HTMLDivElement) => {
    lastProgScrollAtRef.current = performance.now();
    el.scrollTop = el.scrollHeight;
    prevTopRef.current = el.scrollTop;
  };

  // Force re-stick whenever the user posts a new message.
  // Send pushes [userMsg, assistantPlaceholder] together, so the freshest
  // user message is not always at messages[len-1].
  useEffect(() => {
    let freshUserId: string | null = null;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i]!.role === "user") {
        freshUserId = messages[i]!.id;
        break;
      }
    }
    if (!freshUserId || freshUserId === lastUserMsgIdRef.current) return;
    lastUserMsgIdRef.current = freshUserId;
    stickRef.current = true;
    const el = containerRef.current;
    if (el) scrollToBottom(el);
  }, [messages]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !stickRef.current) return;
    scrollToBottom(el);
  }, [messages, partsMap]);

  // While streaming, glue the view to the bottom on every frame. The text
  // reveal (StreamingMarkdown RAF) and markdown reflow grow content height
  // between store updates; pinning only on [messages, partsMap] lets the
  // tail drift toward the composer and snap back — the visible jitter. A
  // per-frame pin keeps the last line at a constant offset. Respects a user
  // scroll-up (stickRef flipped false by the wheel/scroll handlers).
  useEffect(() => {
    if (!isStreaming) return;
    const el = containerRef.current;
    if (!el) return;
    let raf = 0;
    const pin = () => {
      if (stickRef.current) {
        lastProgScrollAtRef.current = performance.now();
        el.scrollTop = el.scrollHeight;
        prevTopRef.current = el.scrollTop;
      }
      raf = requestAnimationFrame(pin);
    };
    raf = requestAnimationFrame(pin);
    return () => cancelAnimationFrame(raf);
  }, [isStreaming]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    stickRef.current = true;
    lastUserMsgIdRef.current = null;
    scrollToBottom(el);
  }, [activeId]);

  const lastIdx = messages.length - 1;

  // Gate phase: while the upload runs the session isn't active yet
  // (activeId flips only when sendMessage fires, after the upload), so the
  // optimistic bubble + running step must render WITHOUT a sessionId match.
  // `starting` scopes it to the in-flight send so a stale heroSync from an
  // earlier session can't resurface on a fresh hero.
  const showGate = heroSync !== null && messages.length === 0 && starting;
  // Settled step: once the real turn lands, splice the step into the turn
  // it belongs to. Here we DO match the session so it can't bleed onto an
  // unrelated chat opened later. The cross-turn rail bridge then connects
  // its dot down to "Рассуждение".
  const showSpliced = heroSync !== null && heroSync.sessionId === activeId;
  const firstAssistantIdx = showSpliced
    ? messages.findIndex((m) => m.role === "assistant")
    : -1;

  const syncLabel = (() => {
    if (!heroSync) return "";
    if (heroSync.kind === "files") {
      if (heroSync.status === "done") return "Файлы прикреплены";
      return heroSync.total > 0
        ? `Прикрепляю файлы · ${heroSync.current}/${heroSync.total}`
        : "Прикрепляю файлы…";
    }
    const folder = heroSync.folderName;
    const where = folder ? ` из папки «${folder}»` : "";
    if (heroSync.status === "done") {
      return folder ? `Папка «${folder}» синхронизирована` : "Файлы синхронизированы";
    }
    if (heroSync.total > 0) {
      return `Синхронизирую файлы${where} · ${heroSync.current}/${heroSync.total}`;
    }
    return `Синхронизирую файлы${where}…`;
  })();

  const syncTurn = heroSync ? (
    <div key="hero-sync" className="turn">
      <div className="msg msg-assistant">
        <div className={`tool-step ${heroSync!.status === "done" ? "tool-completed" : "tool-running"}`}>
          <div className="tool-step-body">
            <div className="tool-step-label" aria-live="polite">{syncLabel}</div>
          </div>
        </div>
      </div>
    </div>
  ) : null;

  if (!activeId) {
    return (
      <div ref={containerRef} className="chat-messages">
        {showGate ? (
          <>
            <div className="turn">
              <div className="msg msg-user">{heroSync!.text}</div>
            </div>
            {syncTurn}
          </>
        ) : (
          !starting && <div className="chat-empty">Выберите или создайте сессию</div>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="chat-messages">
      {showGate && (
        <>
          <div className="turn">
            <div className="msg msg-user">{heroSync!.text}</div>
          </div>
          {syncTurn}
        </>
      )}
      {messages.length === 0 && !isStreaming && !showGate && <div className="chat-empty">Начните диалог</div>}
      {messages.map((msg, i) => {
        const parts = partsMap[msg.id] ?? [];
        return (
          <Fragment key={msg.id}>
            {i === firstAssistantIdx && syncTurn}
            <div className="turn">
              <TurnView
                parts={parts}
                role={msg.role}
                isStreaming={isStreaming && i === lastIdx}
                sessionId={msg.sessionId}
                traceId={msg.traceId}
                feedback={msg.feedback}
              />
            </div>
          </Fragment>
        );
      })}
      <div className="chat-messages-end" />
    </div>
  );
}
